import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createCustomPaymentLink, listAllPayments, listAllSubscriptions } from "./stripe.server";

async function assertAdmin(ctx: {
  supabase: import("@supabase/supabase-js").SupabaseClient;
  userId: string;
}) {
  const { data, error } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (error) throw new Error("Role check failed: " + error.message);
  if (!data) throw new Error("Forbidden");
}

export const getAdminBootstrap = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    const { data } = await ctx.supabase.rpc("has_role", {
      _user_id: ctx.userId,
      _role: "admin",
    });
    return { isAdmin: !!data, userId: ctx.userId };
  });

export type AdminClient = {
  origin: "stripe" | "invited";
  subscription_id: string | null;
  status: string;
  product_name: string | null;
  plan_key: string | null;
  interval: string | null;
  amount: number | null;
  currency: string | null;
  customer_id: string | null;
  customer_email: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  created: string;
  profile_id: string | null;
  client_status: string | null;
  client_status_note: string | null;
  quoted_amount_cents: number | null;
  quoted_currency: string | null;
  pending_payment_link_url: string | null;
};

export type AdminPayment = {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created: string;
  description: string | null;
  customer_email: string | null;
  receipt_url: string | null;
};

export type AdminLead = {
  source: "contact" | "intake";
  id: string;
  created_at: string;
  email: string | null;
  name: string | null;
  summary: string;
  status: string | null;
  thread_id?: string | null;
  admin_status: "pending" | "confirmed" | "denied";
  admin_price_cents: number | null;
  admin_currency: string;
  stripe_payment_link_url: string | null;
};

export type AdminData = {
  clients: AdminClient[];
  payments: AdminPayment[];
  leads: AdminLead[];
};

export const getAdminData = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AdminData> => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);

    const [subs, payments, profilesRes, statusesRes, contactsRes, threadsRes, adminRolesRes] = await Promise.all([
      listAllSubscriptions(),
      listAllPayments(),
      ctx.supabase.from("profiles").select("id, email, name, status, stripe_customer_id, created_at"),
      ctx.supabase.from("client_statuses").select("profile_id, status, note"),
      ctx.supabase
        .from("contact_submissions")
        .select("id, created_at, email, name, message, budget, admin_status, admin_price_cents, admin_currency, stripe_payment_link_url")
        .order("created_at", { ascending: false })
        .limit(100),
      ctx.supabase
        .from("intake_threads")
        .select("id, created_at, email, name, goal, project_url, submitted_at, admin_status, admin_price_cents, admin_currency, stripe_payment_link_url")
        .order("created_at", { ascending: false })
        .limit(100),
      ctx.supabase.from("user_roles").select("user_id").eq("role", "admin"),
    ]);

    const adminUserIds = new Set<string>(
      ((adminRolesRes.data ?? []) as Array<{ user_id: string }>).map((r) => r.user_id),
    );
    const adminEmails = new Set<string>();

    type ProfileRow = {
      id: string;
      email: string;
      name: string | null;
      status: string | null;
      stripe_customer_id: string | null;
      created_at: string | null;
    };
    const profileRows = (profilesRes.data ?? []) as ProfileRow[];
    const profileByCustomer = new Map<string, { id: string; email: string }>();
    const profileByEmail = new Map<string, string>();
    
    for (const p of profileRows) {
      if (p.stripe_customer_id) profileByCustomer.set(p.stripe_customer_id, { id: p.id, email: p.email });
      if (p.email) profileByEmail.set(p.email.toLowerCase(), p.id);
      if (adminUserIds.has(p.id) && p.email) adminEmails.add(p.email.toLowerCase());
    }
    const statusByProfile = new Map<string, { status: string; note: string | null }>();
    for (const s of (statusesRes.data ?? []) as Array<{
      profile_id: string;
      status: string;
      note: string | null;
    }>) {
      statusByProfile.set(s.profile_id, { status: s.status, note: s.note });
    }

    // Build a per-email quote map: most recent confirmed lead with a price.
    const quoteByEmail = new Map<
      string,
      { price_cents: number; currency: string; payment_link_url: string | null; created_at: string }
    >();
    const allLeadRows = [
      ...((contactsRes.data ?? []) as Array<{
        email: string | null;
        admin_status: string | null;
        admin_price_cents: number | null;
        admin_currency: string | null;
        stripe_payment_link_url: string | null;
        created_at: string;
      }>),
      ...((threadsRes.data ?? []) as Array<{
        email: string | null;
        admin_status: string | null;
        admin_price_cents: number | null;
        admin_currency: string | null;
        stripe_payment_link_url: string | null;
        created_at: string;
      }>),
    ];
    for (const row of allLeadRows) {
      if (!row.email || row.admin_status !== "confirmed" || row.admin_price_cents == null) continue;
      const key = row.email.toLowerCase();
      const prev = quoteByEmail.get(key);
      if (!prev || prev.created_at < row.created_at) {
        quoteByEmail.set(key, {
          price_cents: row.admin_price_cents,
          currency: row.admin_currency ?? "usd",
          payment_link_url: row.stripe_payment_link_url,
          created_at: row.created_at,
        });
      }
    }

    const profileIdsWithStripeClient = new Set<string>();
    const stripeClients: AdminClient[] = subs
      .filter((s) => {
        const profileId =
          profileByCustomer.get(s.customer)?.id ??
          (s.customer_email ? profileByEmail.get(s.customer_email.toLowerCase()) ?? null : null);
        if (profileId && adminUserIds.has(profileId)) return false;
        if (s.customer_email && adminEmails.has(s.customer_email.toLowerCase())) return false;
        return true;
      })
      .map((s) => {
      const price = s.items?.data?.[0]?.price;
      let profileId =
        profileByCustomer.get(s.customer)?.id ??
        (s.customer_email ? profileByEmail.get(s.customer_email.toLowerCase()) ?? null : null);
      profileId = profileId ?? null;
      if (profileId) profileIdsWithStripeClient.add(profileId);
      const status = profileId ? statusByProfile.get(profileId) : undefined;
      return {
        origin: "stripe" as const,
        subscription_id: s.id,
        status: s.status,
        product_name: s.product_name,
        plan_key: price?.lookup_key ?? null,
        interval: price?.recurring?.interval ?? null,
        amount: price?.unit_amount ?? null,
        currency: price?.currency ?? null,
        customer_id: s.customer,
        customer_email: s.customer_email,
        current_period_end: s.current_period_end
          ? new Date(s.current_period_end * 1000).toISOString()
          : null,
        cancel_at_period_end: s.cancel_at_period_end,
        created: new Date(s.created * 1000).toISOString(),
        profile_id: profileId,
        client_status: status?.status ?? null,
        client_status_note: status?.note ?? null,
        quoted_amount_cents: null,
        quoted_currency: null,
        pending_payment_link_url: null,
      };
    });

    const INVITED_STATUSES = new Set(["invited", "onboarding", "reviewing", "active", "paused"]);
    const invitedClients: AdminClient[] = profileRows
      .filter((p) => !adminUserIds.has(p.id))
      .filter((p) => !profileIdsWithStripeClient.has(p.id))
      .filter((p) => INVITED_STATUSES.has((p.status ?? "").toLowerCase()))
      .map((p) => {
        const cs = statusByProfile.get(p.id);
        const quote = p.email ? quoteByEmail.get(p.email.toLowerCase()) : undefined;
        return {
          origin: "invited" as const,
          subscription_id: null,
          status: p.status ?? "invited",
          product_name: quote ? "Custom proposal" : "Invited client",
          plan_key: null,
          interval: null,
          amount: null,
          currency: null,
          customer_id: null,
          customer_email: p.email,
          current_period_end: null,
          cancel_at_period_end: false,
          created: p.created_at ?? new Date().toISOString(),
          profile_id: p.id,
          client_status: cs?.status ?? null,
          client_status_note: cs?.note ?? null,
          quoted_amount_cents: quote?.price_cents ?? null,
          quoted_currency: quote?.currency ?? null,
          pending_payment_link_url: quote?.payment_link_url ?? null,
        };
      });

    const clients: AdminClient[] = [...stripeClients, ...invitedClients];

    const adminPayments: AdminPayment[] = payments.map((p) => ({
      id: p.id,
      amount: p.amount,
      currency: p.currency,
      status: p.status,
      created: new Date(p.created * 1000).toISOString(),
      description: p.description,
      customer_email: p.customer_email,
      receipt_url: p.receipt_url,
    }));

    function normalizeAdminStatus(v: string | null | undefined): AdminLead["admin_status"] {
      return v === "confirmed" || v === "denied" ? v : "pending";
    }

    const leads: AdminLead[] = [
      ...((contactsRes.data ?? []) as Array<{
        id: string;
        created_at: string;
        email: string;
        name: string;
        message: string;
        budget: string | null;
        admin_status: string | null;
        admin_price_cents: number | null;
        admin_currency: string | null;
        stripe_payment_link_url: string | null;
      }>).map((c) => ({
        source: "contact" as const,
        id: c.id,
        created_at: c.created_at,
        email: c.email,
        name: c.name,
        summary: `${c.message.slice(0, 180)}${c.message.length > 180 ? "…" : ""}`,
        status: c.budget ? `Budget: ${c.budget}` : null,
        admin_status: normalizeAdminStatus(c.admin_status),
        admin_price_cents: c.admin_price_cents,
        admin_currency: c.admin_currency ?? "usd",
        stripe_payment_link_url: c.stripe_payment_link_url,
      })),
      ...((threadsRes.data ?? []) as Array<{
        id: string;
        created_at: string;
        email: string | null;
        name: string | null;
        goal: string | null;
        project_url: string | null;
        submitted_at: string | null;
        admin_status: string | null;
        admin_price_cents: number | null;
        admin_currency: string | null;
        stripe_payment_link_url: string | null;
      }>).map((t) => ({
        source: "intake" as const,
        id: t.id,
        thread_id: t.id,
        created_at: t.created_at,
        email: t.email,
        name: t.name,
        summary: t.goal
          ? `${t.goal.slice(0, 180)}${t.goal.length > 180 ? "…" : ""}`
          : t.project_url ?? "AI intake conversation",
        status: t.submitted_at ? "Submitted" : "In progress",
        admin_status: normalizeAdminStatus(t.admin_status),
        admin_price_cents: t.admin_price_cents,
        admin_currency: t.admin_currency ?? "usd",
        stripe_payment_link_url: t.stripe_payment_link_url,
      })),
    ]
      // Confirmed leads graduate to the Clients tab — don't double-list them here.
      .filter((l) => l.admin_status !== "confirmed")
      .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));

    return { clients, payments: adminPayments, leads };
  });

export const updateClientStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: {
      profileId: string;
      status: "reviewing" | "onboarding" | "active" | "paused" | "completed";
      note?: string | null;
    }) => {
      const allowed = ["reviewing", "onboarding", "active", "paused", "completed"];
      if (!allowed.includes(data.status)) throw new Error("Invalid status");
      if (!data.profileId) throw new Error("Missing profileId");
      return data;
    },
  )
  .handler(async ({ data, context }): Promise<{ ok: true } | { error: string }> => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { error } = await ctx.supabase
      .from("client_statuses")
      .upsert(
        {
          profile_id: data.profileId,
          status: data.status,
          note: data.note ?? null,
          updated_by: ctx.userId,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "profile_id" },
      );
    if (error) return { error: error.message };
    return { ok: true };
  });

// Convert a lead (intake or contact submission) into a profile so an admin
// can attach a client_status. Returns the new/existing profile id.
export const ensureProfileForLead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { email: string; name?: string | null }) => {
    const email = (data.email ?? "").trim().toLowerCase();
    if (!email) throw new Error("Email required");
    return { email, name: data.name ?? null };
  })
  .handler(async ({ data, context }): Promise<{ profileId: string } | { error: string }> => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { data: existing } = await ctx.supabase
      .from("profiles")
      .select("id")
      .eq("email", data.email)
      .maybeSingle();
    if (existing?.id) return { profileId: existing.id as string };
    return { error: "No profile yet — this lead has not signed in. They need to log in once first." };
  });

// Admin creates a custom plan (product+price) and returns a Stripe Payment
// Link to send to the customer manually. Supports one-off or recurring.
export const createAdminPaymentLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: {
      customerEmail: string;
      customerName?: string;
      planName: string;
      amountCents: number;
      currency?: string;
      billingCycle: "one_time" | "month" | "year";
    }) => {
      const email = (data.customerEmail ?? "").trim().toLowerCase();
      const planName = (data.planName ?? "").trim();
      if (!email || !/.+@.+\..+/.test(email)) throw new Error("Valid email required");
      if (!planName) throw new Error("Plan name required");
      if (!Number.isFinite(data.amountCents) || data.amountCents < 50)
        throw new Error("Amount must be at least 50 cents");
      if (data.amountCents > 100_000_00) throw new Error("Amount too large");
      if (!["one_time", "month", "year"].includes(data.billingCycle))
        throw new Error("Invalid billing cycle");
      const currency = (data.currency ?? "usd").toLowerCase();
      if (!/^[a-z]{3}$/.test(currency)) throw new Error("Invalid currency");
      return {
        customerEmail: email,
        customerName: (data.customerName ?? "").trim() || undefined,
        planName,
        amountCents: Math.round(data.amountCents),
        currency,
        billingCycle: data.billingCycle,
      };
    },
  )
  .handler(
    async ({
      data,
      context,
    }): Promise<{ url: string; paymentLinkId: string } | { error: string }> => {
      const ctx = context as {
        supabase: import("@supabase/supabase-js").SupabaseClient;
        userId: string;
      };
      await assertAdmin(ctx);
      const result = await createCustomPaymentLink(data);
      if ("error" in result) return { error: result.error };
      return { url: result.url, paymentLinkId: result.paymentLinkId };
    },
  );

// Admin invites a client to the dashboard: provisions an auth user/profile
// (status='invited') and emails a set-password link. Only admin-invited
// accounts can sign in to the dashboard.
export const inviteAdminClient = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: { email: string; name?: string; redirectTo: string }) => {
      const email = (data.email ?? "").trim().toLowerCase();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) throw new Error("Valid email required");
      if (!data.redirectTo || !/^https?:\/\//.test(data.redirectTo))
        throw new Error("Invalid redirect URL");
      return {
        email,
        name: (data.name ?? "").trim() || undefined,
        redirectTo: data.redirectTo,
      };
    },
  )
  .handler(
    async ({
      data,
      context,
    }): Promise<{ ok: true; userId: string; emailSent: boolean } | { error: string }> => {
      const ctx = context as {
        supabase: import("@supabase/supabase-js").SupabaseClient;
        userId: string;
      };
      await assertAdmin(ctx);

      const { ensureCheckoutProfile } = await import("./checkout-account.server");
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      let userId: string;
      try {
        const ensured = await ensureCheckoutProfile({
          email: data.email,
          name: data.name ?? null,
          status: "invited",
        });
        userId = ensured.userId;
      } catch (e) {
        return { error: e instanceof Error ? e.message : "Failed to create account" };
      }

      // Force status to 'invited' even if a profile already existed under a
      // lower-trust status (lead/checkout_started). Don't downgrade active/paid.
      const { data: existing } = await supabaseAdmin
        .from("profiles")
        .select("status")
        .eq("id", userId)
        .maybeSingle();
      const currentStatus = (existing as { status?: string | null } | null)?.status ?? "";
      if (currentStatus !== "active" && currentStatus !== "paid") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (supabaseAdmin.from("profiles") as any)
          .update({ status: "invited" })
          .eq("id", userId);
      }

      await supabaseAdmin.auth.admin.updateUserById(userId, { email_confirm: true });

      let emailSent = false;
      try {
        const { error } = await supabaseAdmin.auth.resetPasswordForEmail(data.email, {
          redirectTo: data.redirectTo,
        });
        if (error) console.error("invite email failed", error);
        else emailSent = true;
      } catch (e) {
        console.error("invite email error", e);
      }

      return { ok: true, userId, emailSent };
    },
  );


type LeadSource = "contact" | "intake";
function leadTable(source: LeadSource): "contact_submissions" | "intake_threads" {
  return source === "contact" ? "contact_submissions" : "intake_threads";
}

export const updateAdminLead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: {
      source: LeadSource;
      id: string;
      adminStatus?: "pending" | "confirmed" | "denied";
      priceCents?: number | null;
      currency?: string;
    }) => {
      if (!["contact", "intake"].includes(data.source)) throw new Error("Invalid source");
      if (!data.id) throw new Error("Missing id");
      if (data.adminStatus && !["pending", "confirmed", "denied"].includes(data.adminStatus))
        throw new Error("Invalid status");
      if (
        data.priceCents != null &&
        (!Number.isFinite(data.priceCents) || data.priceCents < 0 || data.priceCents > 100_000_00)
      )
        throw new Error("Invalid price");
      const currency = (data.currency ?? "usd").toLowerCase();
      if (!/^[a-z]{3}$/.test(currency)) throw new Error("Invalid currency");
      return { ...data, currency };
    },
  )
  .handler(async ({ data, context }): Promise<{ ok: true } | { error: string }> => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const patch: Record<string, unknown> = {};
    if (data.adminStatus) patch.admin_status = data.adminStatus;
    if (data.priceCents !== undefined) {
      patch.admin_price_cents = data.priceCents;
      patch.admin_currency = data.currency;
    }
    if (Object.keys(patch).length === 0) return { ok: true };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabaseAdmin.from(leadTable(data.source)) as any)
      .update(patch)
      .eq("id", data.id);
    if (error) return { error: error.message };
    return { ok: true };
  });

export const deleteAdminLead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { source: LeadSource; id: string }) => {
    if (!["contact", "intake"].includes(data.source)) throw new Error("Invalid source");
    if (!data.id) throw new Error("Missing id");
    return data;
  })
  .handler(async ({ data, context }): Promise<{ ok: true } | { error: string }> => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    if (data.source === "intake") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabaseAdmin.from("intake_messages") as any).delete().eq("thread_id", data.id);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabaseAdmin.from(leadTable(data.source)) as any)
      .delete()
      .eq("id", data.id);
    if (error) return { error: error.message };
    return { ok: true };
  });

// ---------- Confirm / Deny (with email side-effects) ----------

function formatMoneyForEmail(cents: number, currency: string): string {
  const sym = currency.toLowerCase() === "eur" ? "€" : currency.toLowerCase() === "gbp" ? "£" : "$";
  return `${sym}${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })} ${currency.toUpperCase()}`;
}

// Confirm a lead, provision an invited dashboard account for the requester,
// create a Stripe payment link for the quoted price, save the link on the
// lead row, then send a branded proposal email with a set-password link.
export const confirmAdminLead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: {
      source: LeadSource;
      id: string;
      redirectTo: string;
    }) => {
      if (!["contact", "intake"].includes(data.source)) throw new Error("Invalid source");
      if (!data.id) throw new Error("Missing id");
      if (!data.redirectTo || !/^https?:\/\//.test(data.redirectTo))
        throw new Error("Invalid redirect URL");
      return data;
    },
  )
  .handler(
    async ({
      data,
      context,
    }): Promise<
      | { ok: true; userId: string; paymentLinkUrl: string | null; emailSent: boolean }
      | { error: string }
    > => {
      const ctx = context as {
        supabase: import("@supabase/supabase-js").SupabaseClient;
        userId: string;
      };
      await assertAdmin(ctx);

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: leadRow, error: leadErr } = await (supabaseAdmin.from(leadTable(data.source)) as any)
        .select("id, email, name, admin_price_cents, admin_currency, stripe_payment_link_url, stripe_payment_link_id")
        .eq("id", data.id)
        .maybeSingle();
      if (leadErr) return { error: leadErr.message };
      if (!leadRow) return { error: "Request not found" };

      const email = (leadRow.email as string | null)?.trim().toLowerCase();
      if (!email) return { error: "Request has no email address" };
      const name = (leadRow.name as string | null) ?? null;
      const priceCents = leadRow.admin_price_cents as number | null;
      const currency = (leadRow.admin_currency as string | null) ?? "usd";

      if (priceCents == null || priceCents < 50) {
        return { error: "Set a price (at least 50 cents) before confirming" };
      }

      const { ensureCheckoutProfile } = await import("./checkout-account.server");

      // 1. Provision auth user + profile in 'invited' status.
      let userId: string;
      try {
        const ensured = await ensureCheckoutProfile({
          email,
          name,
          status: "invited",
        });
        userId = ensured.userId;
      } catch (e) {
        return { error: e instanceof Error ? e.message : "Failed to create account" };
      }

      // Force status to 'invited' (unless already paid/active).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: existing } = await (supabaseAdmin.from("profiles") as any)
        .select("status")
        .eq("id", userId)
        .maybeSingle();
      const currentStatus = (existing as { status?: string | null } | null)?.status ?? "";
      if (currentStatus !== "active" && currentStatus !== "paid") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (supabaseAdmin.from("profiles") as any)
          .update({ status: "invited" })
          .eq("id", userId);
      }
      await supabaseAdmin.auth.admin.updateUserById(userId, { email_confirm: true });

      // 2. Generate (or reuse) the Stripe Payment Link for the quote.
      let paymentLinkUrl: string | null = (leadRow.stripe_payment_link_url as string | null) ?? null;
      if (!paymentLinkUrl) {
        const planName = `Custom proposal · ${name ?? email}`;
        const linkRes = await createCustomPaymentLink({
          planName,
          amountCents: priceCents,
          currency,
          billingCycle: "one_time",
          customerEmail: email,
          customerName: name ?? undefined,
        });
        if ("error" in linkRes) {
          // Continue — we still want to invite the client; admin can retry.
          console.error("Confirm: failed to create payment link", linkRes.error);
        } else {
          paymentLinkUrl = linkRes.url;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await (supabaseAdmin.from(leadTable(data.source)) as any)
            .update({
              stripe_payment_link_url: linkRes.url,
              stripe_payment_link_id: linkRes.paymentLinkId,
            })
            .eq("id", data.id);
        }
      }

      // 3. Mark the lead as confirmed.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabaseAdmin.from(leadTable(data.source)) as any)
        .update({ admin_status: "confirmed" })
        .eq("id", data.id);

      // 4. Generate a password-setup link and send the branded proposal email.
      let emailSent = false;
      try {
        // Generate a recovery link via admin API so we can inline it in our
        // proposal email instead of relying on the auth-email path.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: linkData, error: linkErr } = await (supabaseAdmin.auth.admin as any)
          .generateLink({
            type: "recovery",
            email,
            options: { redirectTo: data.redirectTo },
          });
        const setPasswordUrl: string | null =
          linkErr || !linkData?.properties?.action_link
            ? null
            : (linkData.properties.action_link as string);

        if (setPasswordUrl) {
          const { ProposalEmail } = await import("@/lib/email-templates/proposal");
          const { enqueueBrandedEmail } = await import("@/lib/email/transactional.server");
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const React = (await import("react")) as any;
          const res = await enqueueBrandedEmail({
            to: email,
            subject: `Your proposal from the Vibing Company — ${formatMoneyForEmail(priceCents, currency)}`,
            label: "proposal",
            element: React.createElement(ProposalEmail, {
              recipientName: name,
              priceLabel: formatMoneyForEmail(priceCents, currency),
              summary: paymentLinkUrl
                ? "Sign in to your dashboard to review and complete payment."
                : "Sign in to your dashboard to review next steps.",
              setPasswordUrl,
            }),
          });
          emailSent = !("error" in res);
        }
      } catch (e) {
        console.error("Confirm: failed to send proposal email", e);
      }

      return { ok: true, userId, paymentLinkUrl, emailSent };
    },
  );

export const denyAdminLead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { source: LeadSource; id: string }) => {
    if (!["contact", "intake"].includes(data.source)) throw new Error("Invalid source");
    if (!data.id) throw new Error("Missing id");
    return data;
  })
  .handler(
    async ({
      data,
      context,
    }): Promise<{ ok: true; emailSent: boolean } | { error: string }> => {
      const ctx = context as {
        supabase: import("@supabase/supabase-js").SupabaseClient;
        userId: string;
      };
      await assertAdmin(ctx);

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: leadRow, error: leadErr } = await (supabaseAdmin.from(leadTable(data.source)) as any)
        .select("email, name")
        .eq("id", data.id)
        .maybeSingle();
      if (leadErr) return { error: leadErr.message };
      if (!leadRow) return { error: "Request not found" };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: updateErr } = await (supabaseAdmin.from(leadTable(data.source)) as any)
        .update({ admin_status: "denied" })
        .eq("id", data.id);
      if (updateErr) return { error: updateErr.message };

      const email = (leadRow.email as string | null)?.trim().toLowerCase();
      const name = (leadRow.name as string | null) ?? null;
      let emailSent = false;
      if (email) {
        try {
          const { RejectionEmail } = await import("@/lib/email-templates/rejection");
          const { enqueueBrandedEmail } = await import("@/lib/email/transactional.server");
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const React = (await import("react")) as any;
          const res = await enqueueBrandedEmail({
            to: email,
            subject: "Update on your request to the Vibing Company",
            label: "rejection",
            element: React.createElement(RejectionEmail, { recipientName: name }),
          });
          emailSent = !("error" in res);
        } catch (e) {
          console.error("Deny: failed to send rejection email", e);
        }
      }
      return { ok: true, emailSent };
    },
  );

// ---------- CRM: manual client, detail view, hard delete ----------

export type ClientDetail = {
  profile: {
    id: string;
    email: string;
    name: string | null;
    status: string;
    created_at: string | null;
    stripe_customer_id: string | null;
  };
  status: { status: string; note: string | null } | null;
  proposals: Array<{
    id: string;
    number: string;
    title: string;
    status: string;
    token: string;
    currency: string;
    amount_total_cents: number | null;
    created_at: string;
  }>;
  invoices: Array<{
    id: string;
    number: string;
    status: string;
    currency: string;
    gross_cents: number;
    issued_at: string;
  }>;
  orders: Array<{
    id: string;
    status: string;
    amount_total: number | null;
    currency: string | null;
    created_at: string;
  }>;
  leads: Array<{
    source: LeadSource;
    id: string;
    created_at: string;
    summary: string;
    admin_status: string;
  }>;
};

export const getClientDetail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { profileId: string }) => {
    if (!data.profileId) throw new Error("Missing profile id");
    return { profileId: data.profileId };
  })
  .handler(async ({ data, context }): Promise<ClientDetail | { error: string }> => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: profile, error } = await supabaseAdmin
      .from("profiles")
      .select("id, email, name, status, created_at, stripe_customer_id")
      .eq("id", data.profileId)
      .maybeSingle();
    if (error) return { error: error.message };
    if (!profile) return { error: "Client not found" };

    const p = profile as ClientDetail["profile"];
    const emailLc = (p.email ?? "").toLowerCase();

    const [statusRes, proposalsRes, invoicesRes, ordersRes, contactsRes, threadsRes] =
      await Promise.all([
        supabaseAdmin
          .from("client_statuses")
          .select("status, note")
          .eq("profile_id", p.id)
          .maybeSingle(),
        supabaseAdmin
          .from("proposals")
          .select("id, number, title, status, token, currency, amount_total_cents, created_at, profile_id, client_email")
          .or(`profile_id.eq.${p.id},client_email.eq.${emailLc}`)
          .order("created_at", { ascending: false }),
        supabaseAdmin
          .from("invoices")
          .select("id, number, status, currency, gross_cents, issued_at")
          .eq("profile_id", p.id)
          .order("issued_at", { ascending: false }),
        supabaseAdmin
          .from("orders")
          .select("id, status, amount_total, currency, created_at")
          .eq("profile_id", p.id)
          .order("created_at", { ascending: false }),
        supabaseAdmin
          .from("contact_submissions")
          .select("id, created_at, message, admin_status")
          .eq("email", emailLc)
          .order("created_at", { ascending: false }),
        supabaseAdmin
          .from("intake_threads")
          .select("id, created_at, goal, admin_status")
          .eq("email", emailLc)
          .order("created_at", { ascending: false }),
      ]);

    /* eslint-disable @typescript-eslint/no-explicit-any */
    return {
      profile: p,
      status: (statusRes.data as any) ?? null,
      proposals: ((proposalsRes.data ?? []) as any[]).map((r) => ({
        id: r.id,
        number: r.number,
        title: r.title,
        status: r.status,
        token: r.token,
        currency: r.currency,
        amount_total_cents: r.amount_total_cents,
        created_at: r.created_at,
      })),
      invoices: (invoicesRes.data ?? []) as any,
      orders: (ordersRes.data ?? []) as any,
      leads: [
        ...((contactsRes.data ?? []) as any[]).map((c) => ({
          source: "contact" as const,
          id: c.id,
          created_at: c.created_at,
          summary: String(c.message ?? "").slice(0, 200),
          admin_status: c.admin_status ?? "pending",
        })),
        ...((threadsRes.data ?? []) as any[]).map((t) => ({
          source: "intake" as const,
          id: t.id,
          created_at: t.created_at,
          summary: String(t.goal ?? "AI intake conversation").slice(0, 200),
          admin_status: t.admin_status ?? "pending",
        })),
      ].sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at)),
    };
    /* eslint-enable @typescript-eslint/no-explicit-any */
  });

// Creates a client record by hand. Optionally emails a set-password link.
export const createManualClient = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: {
      email: string;
      name?: string;
      status?: string;
      sendInvite?: boolean;
      redirectTo?: string;
    }) => {
      const email = (data.email ?? "").trim().toLowerCase();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) throw new Error("Valid email required");
      const status = (data.status ?? "invited").trim().toLowerCase();
      if (!["invited", "onboarding", "reviewing", "active", "paused"].includes(status))
        throw new Error("Invalid status");
      if (data.sendInvite && !/^https?:\/\//.test(data.redirectTo ?? ""))
        throw new Error("Invalid redirect URL");
      return {
        email,
        name: (data.name ?? "").trim() || undefined,
        status,
        sendInvite: !!data.sendInvite,
        redirectTo: data.redirectTo,
      };
    },
  )
  .handler(
    async ({
      data,
      context,
    }): Promise<{ ok: true; profileId: string; emailSent: boolean } | { error: string }> => {
      const ctx = context as {
        supabase: import("@supabase/supabase-js").SupabaseClient;
        userId: string;
      };
      await assertAdmin(ctx);

      const { ensureCheckoutProfile } = await import("./checkout-account.server");
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      let userId: string;
      try {
        const ensured = await ensureCheckoutProfile({
          email: data.email,
          name: data.name ?? null,
          status: data.status,
        });
        userId = ensured.userId;
      } catch (e) {
        return { error: e instanceof Error ? e.message : "Failed to create client" };
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabaseAdmin.from("profiles") as any)
        .update({ status: data.status, ...(data.name ? { name: data.name } : {}) })
        .eq("id", userId);
      await supabaseAdmin.auth.admin.updateUserById(userId, { email_confirm: true });

      let emailSent = false;
      if (data.sendInvite && data.redirectTo) {
        try {
          const { error } = await supabaseAdmin.auth.resetPasswordForEmail(data.email, {
            redirectTo: data.redirectTo,
          });
          emailSent = !error;
          if (error) console.error("manual client invite email failed", error);
        } catch (e) {
          console.error("manual client invite email error", e);
        }
      }

      return { ok: true, profileId: userId, emailSent };
    },
  );

// Hard delete: removes the login + profile. Proposals, invoices and orders
// detach automatically (FK ON DELETE SET NULL) so financial records survive.
export const deleteClient = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { profileId: string; confirmEmail: string }) => {
    if (!data.profileId) throw new Error("Missing profile id");
    if (!data.confirmEmail) throw new Error("Confirmation email required");
    return { profileId: data.profileId, confirmEmail: data.confirmEmail.trim().toLowerCase() };
  })
  .handler(async ({ data, context }): Promise<{ ok: true } | { error: string }> => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);

    if (data.profileId === ctx.userId) return { error: "You cannot delete your own account." };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: profile, error } = await supabaseAdmin
      .from("profiles")
      .select("id, email")
      .eq("id", data.profileId)
      .maybeSingle();
    if (error) return { error: error.message };
    if (!profile) return { error: "Client not found" };
    const p = profile as { id: string; email: string };
    if ((p.email ?? "").toLowerCase() !== data.confirmEmail)
      return { error: "Confirmation email does not match this client." };

    const { data: roles } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", p.id);
    if (((roles ?? []) as Array<{ role: string }>).some((r) => r.role === "admin"))
      return { error: "This account is an admin and cannot be deleted here." };

    const { error: delErr } = await supabaseAdmin.auth.admin.deleteUser(p.id);
    if (delErr) return { error: delErr.message };

    // Profile cascades with the auth user; make sure nothing lingers.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabaseAdmin.from("profiles") as any).delete().eq("id", p.id);

    return { ok: true };
  });
